import { createContext, useContext, useEffect, useRef } from "react"
import { type WebSocketsClient } from "./utils"

export const WebSocketsContext = createContext<WebSocketsClient | null>(null)

export function useWebSockets(): WebSocketsClient | null {
	return useContext(WebSocketsContext)
}

/**
 * Subscribe to one or more events on a private channel for the lifetime of the
 * calling component. `handler` may change every render without re-subscribing.
 */
export function usePrivateChannel<TPayload = unknown>(
	channelName: string | null | undefined,
	eventName: string | string[],
	handler: (payload: TPayload) => void
) {
	const client = useWebSockets()
	const handlerRef = useRef(handler)
	// A stable key so the effect only re-subscribes when the set of events
	// actually changes, not on every render's fresh array literal.
	const eventsKey = (Array.isArray(eventName) ? eventName : [eventName]).join(
		"|"
	)

	useEffect(() => {
		handlerRef.current = handler
	}, [handler])

	useEffect(() => {
		if (!client || !channelName) {
			return
		}
		const channel = client.private(channelName)
		const events = eventsKey.split("|")
		const listener = (payload: TPayload) => handlerRef.current(payload)
		events.forEach((event) => channel.listen(event, listener))
		return () => {
			events.forEach((event) => channel.stopListening(event, listener))
			client.leave(channelName)
		}
	}, [client, channelName, eventsKey])
}
